import React, { useContext, useEffect, useState } from 'react'
import { DContext } from '../../context/DataContext'
import AccountImg from '../../assets/undraw_account.svg'
import LoadingPage from './Loading';
import NavBlock from '../blocks/Admin/NavBlock';
import { normalizeContact } from '../../utils/contact';
import { FiCalendar, FiEdit2, FiEye, FiEyeOff, FiGlobe, FiMail, FiMapPin, FiPhone, FiSave, FiUser, FiXCircle } from 'react-icons/fi';
import MetaData from '../../utils/MetaData';


function Account() {

    const { BeURL, currentUser, setLoader } = useContext(DContext)
    const [user, setUser] = useState(null);
    const [formData, setFormData] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (user) {
            const normalizedContact = normalizeContact(user.contact)
            setFormData({
                fullname: user.fullname,
                email: user.email,
                contact: normalizedContact,
                city: user.city,
                country: user.country,
                password: '',
                confirmPassword: '',
            })
        }
    }, [user])

    useEffect(() => {
        if (Object.entries(currentUser).length > 0) {
            setLoader(true)
            fetch(`${BeURL}/fetch-user/${currentUser.id}`, {
                credentials: "include"
            })
                .then(res => res.json())
                .then(data => {
                    setLoader(false)
                    if (data.success) {
                        console.log("data:", data)
                        setUser(data.user)
                    } else {
                        alert(data.message)
                    }
                })
                .catch(err => {
                    setLoader(false)
                    alert('Trouble in connecting to the Server! Please try again later.')
                    console.log('Error in fetching user:', err)
                })
        }
    }, [BeURL, currentUser, setLoader])

    const handleCancel = () => {
        const normalizedContact = normalizeContact(user.contact)
        setFormData({
            fullname: user.fullname,
            email: user.email,
            contact: normalizedContact,
            city: user.city,
            country: user.country,
            password: '',
            confirmPassword: '',
        });
        setEditMode(false);
        setShowPassword(false);
        setShowConfirmPassword(false);
    };

    // Validate all fields before updating.
    const validateForm = () => {
        const { fullname, email, contact, password, confirmPassword, city, country } = formData;
        console.log("formData:", formData)
        if (!fullname.trim() || !email.trim() || !contact.countryCode.trim() || !contact.number.trim() || !city.trim() || !country.trim()) {
            alert("All fields are required.");
            return false;
        }
        // Simple email validation.
        const emailRegex = /^\S+@\S+\.\S+$/;
        if (!emailRegex.test(email)) {
            alert("Please enter a valid email address.");
            return false;
        }

        if (password.trim() !== '' && password !== confirmPassword) {
            alert('Passwords do not match.');
            return false;
        }

        return true;
    };

    // Handler to update user details.
    const handleUpdate = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoader(true);

        const changePassword = formData.password.trim() !== '';

        fetch(`${BeURL}/update-user/${user.id}`, {
            method: 'PUT', // Change to 'POST' if that's your endpoint's requirement.
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: "include",
            body: JSON.stringify({
                fullname: formData.fullname,
                email: formData.email,
                contact: formData.contact,
                password: formData.password,
                city: formData.city,
                country: formData.country,
                changePassword
            }),
        })
            .then(res => res.json())
            .then(data => {
                setLoader(false);
                alert(data.message);
                if (data.success) {
                    // Update user data with new values.
                    setUser({ ...user, ...formData });
                    setEditMode(false);
                }
            })
            .catch(err => {
                setLoader(false);
                alert("Trouble in connecting to the Server! Please try again later.");
                console.error("Error updating user:", err);
            });
    };

    if (user === null || formData === null) {
        return <LoadingPage />
    }

    return (
        <>
            <MetaData
                title='My Account | Be Alright Krabi'
                description='Manage your Be Alright Krabi account details.'
                canonicalUrl={`${window.location.origin}/account`}
                robots='noindex,nofollow'
            />
            <div className='mx-auto my-6 w-full max-w-6xl px-4 sm:px-6'>
                <NavBlock prev={"/"} />
                <div className='overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl'>
                    <div className='grid gap-0 md:grid-cols-5'>
                        <div className='flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-sky-50 to-blue-100 p-6 md:col-span-2 md:p-8'>
                            <img src={AccountImg} alt='Account Illustration' className='h-auto w-full max-w-[260px]' />
                            <div className='text-center'>
                                <h2 className='text-2xl font-semibold tracking-tight text-slate-900'>Your Account</h2>
                                <p className='mt-2 text-sm text-slate-600'>Manage personal info and contact details from one place.</p>
                            </div>
                        </div>

                        <div className='md:col-span-3'>
                            <div className='p-6 sm:p-8'>
                                <h3 className='mb-6 text-2xl font-bold text-slate-900'>Account Details</h3>
                                <form onSubmit={handleUpdate}>
                                    <div className='grid gap-4 sm:grid-cols-2'>
                                        <div>
                                            <label className='mb-1.5 inline-flex items-center gap-2 text-sm font-medium text-slate-700'>
                                                <FiUser className='text-slate-500' />
                                                Full Name
                                            </label>
                                            <input
                                                type='text'
                                                className='w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-500'
                                                value={formData.fullname}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, fullname: e.target.value })
                                                }
                                                disabled={!editMode}
                                            />
                                        </div>

                                        <div>
                                            <label className='mb-1.5 inline-flex items-center gap-2 text-sm font-medium text-slate-700'>
                                                <FiMail className='text-slate-500' />
                                                Email
                                            </label>
                                            <input
                                                type='email'
                                                className='w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-500'
                                                value={formData.email}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, email: e.target.value })
                                                }
                                                disabled={!editMode}
                                            />
                                        </div>

                                        <div className='sm:col-span-2'>
                                            <label className='mb-1.5 inline-flex items-center gap-2 text-sm font-medium text-slate-700'>
                                                <FiPhone className='text-slate-500' />
                                                Contact
                                            </label>
                                            <div className='grid gap-3 sm:grid-cols-4'>
                                                <input
                                                    type='text'
                                                    className='w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-500'
                                                    value={formData.contact.countryCode}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            contact: { ...formData.contact, countryCode: e.target.value }
                                                        })
                                                    }
                                                    disabled={!editMode}
                                                />
                                                <input
                                                    type='text'
                                                    className='w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-500 sm:col-span-3'
                                                    value={formData.contact.number}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            contact: { ...formData.contact, number: e.target.value }
                                                        })
                                                    }
                                                    disabled={!editMode}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className='mb-1.5 inline-flex items-center gap-2 text-sm font-medium text-slate-700'>
                                                <FiMapPin className='text-slate-500' />
                                                City
                                            </label>
                                            <input
                                                type='text'
                                                className='w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-500'
                                                value={formData.city}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, city: e.target.value })
                                                }
                                                disabled={!editMode}
                                            />
                                        </div>

                                        <div>
                                            <label className='mb-1.5 inline-flex items-center gap-2 text-sm font-medium text-slate-700'>
                                                <FiGlobe className='text-slate-500' />
                                                Country
                                            </label>
                                            <input
                                                type='text'
                                                className='w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-500'
                                                value={formData.country}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, country: e.target.value })
                                                }
                                                disabled={!editMode}
                                            />
                                        </div>
                                    </div>

                                    {editMode && (
                                        <div className='mt-4 grid gap-4 sm:grid-cols-2'>
                                            <div>
                                                <label className='mb-1.5 inline-flex items-center gap-2 text-sm font-medium text-slate-700'>Password (Optional)</label>
                                                <div className='relative'>
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        className='w-full rounded-xl border border-slate-300 px-3 py-2.5 pr-11 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                                                        value={formData.password}
                                                        onChange={(e) =>
                                                            setFormData({ ...formData, password: e.target.value })
                                                        }
                                                    />
                                                    <button
                                                        type='button'
                                                        className='absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700'
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        {showPassword ? <FiEyeOff className='text-lg' /> : <FiEye className='text-lg' />}
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className='mb-1.5 inline-flex items-center gap-2 text-sm font-medium text-slate-700'>Confirm Password</label>
                                                <div className='relative'>
                                                    <input
                                                        type={showConfirmPassword ? 'text' : 'password'}
                                                        className='w-full rounded-xl border border-slate-300 px-3 py-2.5 pr-11 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                                                        value={formData.confirmPassword}
                                                        onChange={(e) =>
                                                            setFormData({ ...formData, confirmPassword: e.target.value })
                                                        }
                                                    />
                                                    <button
                                                        type='button'
                                                        className='absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700'
                                                        onClick={() =>
                                                            setShowConfirmPassword(!showConfirmPassword)
                                                        }
                                                    >
                                                        {showConfirmPassword ? <FiEyeOff className='text-lg' /> : <FiEye className='text-lg' />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className='mt-6 flex flex-wrap items-center gap-3'>
                                        {!editMode ? (
                                            <button
                                                type='button'
                                                className='inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setEditMode(true);
                                                }}
                                            >
                                                <FiEdit2 />
                                                Edit
                                            </button>
                                        ) : (
                                            <>
                                                <button type='submit' className='inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700'>
                                                    <FiSave />
                                                    Update
                                                </button>
                                                <button
                                                    type='button'
                                                    className='inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100'
                                                    onClick={handleCancel}
                                                >
                                                    <FiXCircle />
                                                    Cancel
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    <p className='mt-6 inline-flex items-center gap-2 text-sm text-slate-500'>
                                        <FiCalendar />
                                        Created At: {new Date(user.createdAt).toLocaleString()}
                                    </p>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Account